import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import {
  Autocomplete,
  Avatar,
  Box,
  createFilterOptions,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField as MuiTextField,
} from "@mui/material";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { DataGrid } from "@mui/x-data-grid";

import SearchIcon from "@mui/icons-material/Search";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import { getAllTokenActive, getAllTokenOrder } from "src/api/tokenTransaction";

import font from "src/styles/font";
import device from "src/styles/device";
import {
  Black,
  DarkGrey,
  Green,
  Grey,
  White,
  White01,
  Yellow,
} from "src/styles/color";
import { convertIntToCurrency } from "src/common";
import { UseUpload } from "src/context/UploadProductContext";

import Main from "src/components/ui/Container";
import Loading from "src/components/ui/Loading";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import Chip from "src/components/core/Chip";

const Container = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px;
`;

const SpanHeader = styled.div`
  color: ${Black};
  font-size: ${font.size.xl};
  font-weight: 600;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.lg};
  }
`;

const SpanSubHeader = styled.div`
  color: ${DarkGrey};
  font-size: ${font.size.md};
  font-weight: 400;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const Wrapper = styled.div`
  width: 100%;
  border-radius: 16px;
  border: 1px solid ${White01};
  background-color: ${White};
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px -1px rgba(0, 0, 0, 0.1);
`;

const SpanItemName = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.md};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 500)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const SpanSubItem = styled.div`
  color: ${({ color }) => (color ? color : Grey)};
  font-size: ${font.size.sm};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 400)};

  span {
    font-size: ${font.size.sm};
    font-weight: 400;
  }

  @media ${device.tabletL} {
    font-size: ${font.size.xs};
  }
`;

const WrapperFilter = styled(Wrapper)`
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const WrapperDataGrid = styled(Wrapper)`
  width: 100%;
  height: 600px;

  .MuiDataGrid-root {
    border: 1px solid ${White01};
    border-radius: 16px;
    overflow: hidden;
  }
  .MuiDataGrid-columnHeaders {
    background-color: rgba(249, 250, 251, 0.5);
  }
  .MuiDataGrid-root .MuiDataGrid-columnSeparator--sideRight {
    color: #f5f5f5;
  }
  .MuiDataGrid-root .MuiDataGrid-columnHeader {
    padding: 0 16px;
  }
  .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within {
    outline: none;
  }
  .MuiDataGrid-root .MuiDataGrid-cell {
    padding: 0 16px;
  }
  .MuiDataGrid-root .MuiDataGrid-cell:focus {
    outline: none;
    border-bottom: solid #f5f5f5 1px 0 1px 0;
  }
  .MuiDataGrid-root .MuiDataGrid-row.Mui-selected {
    background-color: #ffffff;
  }
`;

const ImageNoData = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/no+data.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;
const ImageNotFound = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/not+found.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;

const TextNoData = styled.pre`
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
`;

const SpanToken = styled.span`
  font-size: ${font.size.md};
  font-weight: 600;
  margin: 0;
  color: ${({ added, used }) => (added ? Green : used ? Yellow : Black)};
`;

const TokenOrderList = ({ open }) => {
  const { masterdata } = UseUpload();
  const { seller_data } = masterdata;
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [packageInfoList, setPackageInfoList] = useState([]);
  const [tokenTransactionList, setTokenTransactionList] = useState([]);
  const defaultSearch = {
    page: 0,
    page_size: 10,
    keyword: "",
    status: "",
    user: null,
    // expired: null, // ?
  };
  const [dataSearch, setDataSearch] = useState(defaultSearch);
  const [rowCount, setRowCount] = useState(0);
  const chipColors = [
    "default",
    "secondary",
    "info",
    "primary",
    "success",
    "warning",
    "error",
  ];

  const fetchAllPackages = async () => {
    try {
      setIsLoading(true);

      const data = await getAllTokenActive();
      setPackageInfoList(data);
    } catch (error) {
      console.error("Error fetching token packages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTokenTransactionList = async () => {
    try {
      setIsLoading(true);

      const response = await getAllTokenOrder({
        ...dataSearch,
        keyword: dataSearch.keyword || null,
        page: dataSearch.page,
        user: dataSearch?.user?.user?._id || null,
      });

      if (!!response) {
        setTokenTransactionList(response?.orders);
        setRowCount(response?.pagination?.total);
      }
    } catch (error) {
      console.error("Error fetching token transaction list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // only fetch when keyword is empty (to prevent too many request when user is typing keyword)
    if (dataSearch.keyword === "") fetchTokenTransactionList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearch.page, dataSearch.status, dataSearch.user, dataSearch.keyword]);

  const getPackageColor = (packageId) => {
    const index = packageInfoList.findIndex((pkg) => pkg._id === packageId);
    return chipColors[index % chipColors.length];
  };

  function stringToColor(string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
  }

  function stringAvatar(name) {
    return {
      sx: {
        bgcolor: stringToColor(name),
      },
      children: `${name.split(" ")[0][0]}${name.split(" ")[0][1]}`,
    };
  }

  const columns = [
    {
      field: "order_info",
      headerName: "ผู้ขาย / วันที่ทำรายการ",
      flex: 1.8,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              margin: "16px 0",
              outline: "none",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Avatar
              {...(!!params?.row?.user?.seller?.profile_image
                ? { src: params?.row?.user?.seller?.profile_image }
                : stringAvatar(params?.row?.user?.seller?.company_name))}
            />
            <Box
              sx={{
                width: "100%",
                textOverflow: "ellipsis",
              }}
            >
              <SpanItemName mb={8}>
                {params?.row?.user?.seller?.company_name}
              </SpanItemName>

              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: Grey }} />
                <SpanSubItem>
                  {params?.row?.createdAt
                    ? dayjs(params?.row?.createdAt).format("DD/MM/YYYY HH:mm")
                    : "-"}
                </SpanSubItem>
              </Stack>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "package_info",
      headerName: "แพ็คเกจ",
      flex: 1.2,
      headerAlign: "center",
      align: "center",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              margin: "8px",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "center",
            }}
          >
            <Chip
              variant="secondary"
              color={getPackageColor(params?.row?.package?._id)}
              label={params?.row?.package?.name}
              sx={{ mb: 1 }}
            />
            <SpanSubItem>{params?.row?.order_no}</SpanSubItem>
          </Box>
        );
      },
    },
    {
      field: "token_amount",
      headerName: "ได้รับ Token",
      flex: 1,
      headerAlign: "center",
      align: "center",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Box sx={{ textAlign: "center" }}>
            <SpanToken added>{`+${params?.row?.token_amount}`}</SpanToken>

            <SpanSubItem>Tokens</SpanSubItem>
          </Box>
        );
      },
    },
    {
      field: "price",
      headerName: "ยอดเงิน",
      flex: 1,
      headerAlign: "right",
      align: "right",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Box sx={{ textAlign: "right" }}>
            <SpanItemName
              fontWeight={600}
            >{`${convertIntToCurrency(params?.row?.price, true)} ฿`}</SpanItemName>
            <SpanSubItem>
              {params?.row?.payment_method === "credit_card"
                ? `Credit Card`
                : params?.row?.payment_method === "promptpay"
                  ? `PromptPay`
                  : params?.row?.payment_method === "truewallet"
                    ? `TrueWallet`
                    : `Other`}
            </SpanSubItem>
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "สถานะ",
      flex: 1,
      headerAlign: "center",
      align: "center",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Chip
            icon={
              params.row?.status === "PAID" ? (
                <CheckCircleOutlinedIcon />
              ) : params.row?.status === "PENDING" ? (
                <AccessTimeIcon />
              ) : (
                <HighlightOffIcon />
              )
            }
            label={
              params.row?.status === "PAID"
                ? "ชำระแล้ว"
                : params.row?.status === "PENDING"
                  ? "รอชำระ"
                  : params.row?.status === "CANCELLED"
                    ? "ยกเลิก"
                    : params.row?.status === "EXPIRED"
                      ? "หมดอายุ"
                      : "ไม่สำเร็จ"
            }
            variant="secondary"
            color={
              params.row.status === "PAID"
                ? "success"
                : params.row.status === "PENDING"
                  ? "warning"
                  : "error"
            } // ?
            sx={{
              "&.MuiChip-secondary.MuiChip-colorSuccess": {
                color: "#008236",
              },
              "&.MuiChip-secondary.MuiChip-colorWarning": {
                color: "#CA3500",
              },
            }}
          />
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Button
            variant="dark"
            size="small"
            onClick={() => navigate(`/token-order/${params?.row?._id}`)}
          >
            ดูรายละเอียด
          </Button>
        );
      },
    },
  ];

  const handlePageChange = (newPage) => {
    setDataSearch({ ...dataSearch, page: newPage });
  };

  const onSearchButton = () => {
    setDataSearch({ ...dataSearch, page: 0 });
    fetchTokenTransactionList();
  };

  function NoRowsOverlay() {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <ImageNoData></ImageNoData>
        <TextNoData>No Data</TextNoData>
      </Stack>
    );
  }

  function NoResultsOverlay() {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <ImageNotFound></ImageNotFound>
        <TextNoData>Not Found</TextNoData>
      </Stack>
    );
  }

  return (
    <>
      <Main open={open}>
        <Container>
          <Grid container columnSpacing={2} rowSpacing={3}>
            <Grid item xs={12}>
              <SpanHeader mb={2}>ธุรกรรมของผู้ขายทั้งหมด</SpanHeader>
              <SpanSubHeader>
                ตรวจสอบและจัดการประวัติการเติม Token ของผู้ขายทุกรายในระบบ
              </SpanSubHeader>
            </Grid>

            <Grid item xs={12}>
              <WrapperFilter>
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    placeholder="ค้นหาเลขอ้างอิง..."
                    sx={{ width: 400 }}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          id="button-search-quotation"
                          sx={{ padding: 0 }}
                          onClick={() => {}}
                        >
                          <SearchIcon
                            sx={{
                              color: "#BF0013",
                              cursor: "pointer",
                            }}
                          />
                        </IconButton>
                      ),
                    }}
                    value={dataSearch.keyword}
                    onChange={(e) =>
                      setDataSearch({
                        ...dataSearch,
                        keyword: e.target.value,
                      })
                    }
                    onKeyUp={(e) =>
                      e.code === "Enter" ? onSearchButton() : null
                    }
                  />
                </Box>

                <Box
                  sx={{
                    width: "max-content",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FormControl
                    sx={{
                      minWidth: 160,
                      marginRight: 2,
                      "& .MuiInputBase-input": {
                        padding: "12px 16px",
                      },
                      "& .MuiInputLabel-root": {
                        transform: "translate(14px, 12px) scale(1)",
                      },
                      "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiInputLabel-shrink":
                        {
                          transform: "translate(14px, -9px) scale(0.75)",
                        },
                    }}
                  >
                    <InputLabel id="status-select-label">
                      สถานะการเติม
                    </InputLabel>
                    <Select
                      labelId="status-select-label"
                      id="status-select"
                      label="สถานะการเติม"
                      value={dataSearch.status}
                      onChange={(e) =>
                        setDataSearch({
                          ...dataSearch,
                          status: e.target.value,
                          page: 0,
                        })
                      }
                    >
                      <MenuItem value={""}>ทั้งหมด</MenuItem>
                      <MenuItem value={"PENDING"}>รอดำเนินการ</MenuItem>
                      <MenuItem value={"PAID"}>สำเร็จ</MenuItem>
                      <MenuItem value={"CANCELLED"}>ยกเลิก</MenuItem>
                      <MenuItem value={"EXPIRED"}>หมดอายุ</MenuItem>
                      <MenuItem value={"FAILED"}>ล้มเหลว</MenuItem>
                    </Select>
                  </FormControl>

                  <Autocomplete
                    id="search-seller-user"
                    isOptionEqualToValue={(option, value) =>
                      option?.seller?._id === value?._id
                    }
                    renderOption={(props, option) => (
                      <li {...props}>{option?.seller?.company_name}</li>
                    )}
                    options={seller_data}
                    value={dataSearch?.user}
                    onChange={(e, newValue) => {
                      setDataSearch({
                        ...dataSearch,
                        user: newValue,
                        page: 0,
                      });
                    }}
                    getOptionLabel={(option) => {
                      return option?.seller?.company_name;
                    }}
                    filterOptions={createFilterOptions({
                      matchFrom: "any",
                      limit: 100,
                    })}
                    sx={{
                      width: "100%",
                      mr: 2,
                      background: "#fff",
                      ".MuiAutocomplete-option": {
                        p: 3,
                      },
                      "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                        padding: "3.5px 4px 3.5px 6px",
                      },
                      "& .MuiInputLabel-root": {
                        transform: "translate(14px, 12px) scale(1)",
                      },
                      "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiInputLabel-shrink":
                        {
                          transform: "translate(14px, -9px) scale(0.75)",
                        },
                    }}
                    renderInput={(params) => (
                      <MuiTextField
                        className="TextField-without-border-radius"
                        {...params}
                        label="Seller"
                        sx={{
                          minWidth: 180,
                        }}
                      />
                    )}
                  />

                  {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      // label="end date"
                      placeholder="ระบุวันที่"
                      inputFormat="DD-MM-YYYY"
                      value={dataSearch?.expired || null}
                      onChange={(newValue) =>
                        setDataSearch({
                          ...dataSearch,
                          expired: dayjs(newValue).format("DD-MM-YYYY"),
                          page: 0,
                        })
                      }
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          inputProps={{ ...params.inputProps, readOnly: true }}
                          variant="outlined"
                          sx={{
                            minWidth: 180,
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider> */}

                  <Button
                    variant="text"
                    startIcon={<RefreshOutlinedIcon />}
                    sx={{
                      width: "100%",
                    }}
                    onClick={() => setDataSearch(defaultSearch)}
                  >
                    Clear Filter
                  </Button>
                </Box>
              </WrapperFilter>
            </Grid>

            {/* package detail table */}
            <Grid item xs={12}>
              <WrapperDataGrid>
                <DataGrid
                  rows={tokenTransactionList}
                  getRowId={(row) => row._id}
                  getRowHeight={() => "auto"}
                  loading={isLoading}
                  columns={columns}
                  pageSize={10}
                  rowCount={rowCount}
                  paginationMode="server"
                  page={dataSearch.page}
                  onPageChange={(e) => handlePageChange(e)}
                  pagination
                  disableSelectionOnClick
                  components={{
                    NoRowsOverlay,
                    NoResultsOverlay,
                  }}
                  componentsProps={{
                    basePopper: {},
                  }}
                />
              </WrapperDataGrid>
            </Grid>
          </Grid>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default TokenOrderList;
